import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { useLang } from '../i18n'
import { ANCHOR_NAME_MAX, anchorNameKey, normalizeAnchorName, type Anchor } from '../lib/anchors'
import type { ArtefactAnchor } from '../lib/artefact-anchors'

/**
 * The Anchor dialog (AB#350), opened by the Anchor button of the prose toolbar and of the slides
 * contextual bar: Name, then Create (a new anchor) or Rename + Remove (the anchor under the caret or
 * on the selected element). The name is required, at most ANCHOR_NAME_MAX characters, and unique in
 * the artefact, case-insensitively — checked against `existing` (lib/artefact-anchors.ts), so
 * Create / Rename wait until it is read. Removing an anchor keeps its text and its element.
 *
 * `modal`: a dialog over a backdrop (prose); else a non-modal popover the caller places, carrying
 * `className` (the slides editor's `sl-keepedit`, so its text editing is not closed under it).
 */
export interface AnchorDialogProps {
  /** The anchor being edited, or null (a new one). */
  initial: Anchor | null
  /** A new anchor's name: the selected text, cut (lib/anchors.ts#defaultAnchorName). */
  defaultName?: string
  /** Every anchor of the artefact, for the uniqueness check; null while it is being read. */
  existing: readonly ArtefactAnchor[] | null
  /** Create (new) or rename (edit), with the name normalised. */
  onApply: (name: string) => void
  onRemove: () => void
  onClose: () => void
  modal?: boolean
  className?: string
}

export function AnchorDialog({ initial, defaultName = '', existing, onApply, onRemove, onClose, modal = false, className = '' }: AnchorDialogProps) {
  const { t } = useLang()
  const uid = useId()
  const [name, setName] = useState(initial?.name ?? defaultName)
  const [error, setError] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)
  useEffect(() => {
    const prev = modal ? (document.activeElement as HTMLElement | null) : null
    input.current?.focus()
    input.current?.select()
    return () => prev?.focus()
  }, [modal])

  /** Why the typed name cannot be used, or null. */
  const problem = (v: string): string | null => {
    if (!v) return t('an_name_required')
    if (v.length > ANCHOR_NAME_MAX) return t('an_name_too_long').replace('{n}', String(ANCHOR_NAME_MAX))
    const key = anchorNameKey(v)
    if (existing?.some((a) => a.id !== initial?.id && anchorNameKey(a.name) === key)) return t('an_name_taken')
    return null
  }
  const submit = (e?: FormEvent): void => {
    e?.preventDefault()
    if (existing === null) return
    const v = normalizeAnchorName(name)
    const why = problem(v)
    if (why) {
      setError(why)
      input.current?.focus()
      return
    }
    if (initial && v === initial.name) return onClose()
    onApply(v)
  }

  const body = (
    <form onSubmit={submit} noValidate className="anchor-dialog-form">
      <h2 id={`${uid}-title`} className="anchor-dialog-title">
        {t(initial ? 'an_title_edit' : 'an_title_add')}
      </h2>
      <label className="anchor-dialog-field">
        {t('an_name')}
        <input
          ref={input}
          type="text"
          value={name}
          maxLength={ANCHOR_NAME_MAX + 20}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${uid}-error` : undefined}
          onChange={(e) => {
            setName(e.target.value)
            setError(null)
          }}
        />
      </label>
      {existing === null && <p className="anchor-dialog-note">{t('an_checking')}</p>}
      {error && (
        <p id={`${uid}-error`} className="anchor-dialog-error" role="alert">
          {error}
        </p>
      )}
      <div className="anchor-dialog-actions">
        {initial && (
          <button type="button" className="fmtbtn" onClick={onRemove}>
            {t('an_remove')}
          </button>
        )}
        <button type="button" className="fmtbtn" onClick={onClose}>
          {t('lk_cancel')}
        </button>
        <button type="submit" className="fmtbtn anchor-dialog-apply" disabled={existing === null}>
          {t(initial ? 'an_rename' : 'an_create')}
        </button>
      </div>
    </form>
  )
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return
    e.preventDefault()
    e.stopPropagation()
    onClose()
  }

  if (!modal)
    return (
      <div className={`anchor-dialog ${className}`.trim()} role="dialog" aria-labelledby={`${uid}-title`} onKeyDown={onKeyDown}>
        {body}
      </div>
    )
  return createPortal(
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className={`modal anchor-dialog anchor-dialog-modal ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${uid}-title`}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        {body}
      </div>
    </div>,
    document.body,
  )
}
